import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Link2, RefreshCw, Eye, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { PageHeader, DataTable, StatusBadge, fmt } from "../components/bits";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const EMPTY_EMP = { name: "", document: "", position: "", phone: "", salary: "", pay_frequency: "Mensual", expected_start: "08:00", expected_end: "15:00", tolerance_minutes: 10, active: true };
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString("es-DO", { hour: "2-digit", minute: "2-digit" }) : "—");
const PTYPE_LABEL = { mensual: "Mensual", "quincena-1": "1ra Quincena", "quincena-2": "2da Quincena" };

export default function Nomina() {
  const [employees, setEmployees] = useState([]);
  const [punches, setPunches] = useState([]);
  const [runs, setRuns] = useState([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [empOpen, setEmpOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_EMP);
  const [runOpen, setRunOpen] = useState(false);
  const [runForm, setRunForm] = useState({ period: new Date().toISOString().slice(0, 7), period_type: "mensual" });
  const [runDetail, setRunDetail] = useState(null);

  const load = useCallback(() => {
    api.get("/payroll/employees").then((r) => setEmployees(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/payroll/runs").then((r) => setRuns(r.data)).catch(() => {});
  }, []);
  const loadPunches = useCallback(() => {
    api.get(`/payroll/punches?month=${month}`).then((r) => setPunches(r.data)).catch(() => {});
  }, [month]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadPunches(); }, [loadPunches]);

  const punchUrl = (emp) => `${window.location.origin}/ponche/${emp.punch_token}`;

  const copyLink = (emp) => {
    navigator.clipboard.writeText(punchUrl(emp));
    toast.success(`Link de ponche de ${emp.name} copiado`);
  };

  const regenerate = async (emp) => {
    if (!window.confirm(`¿Generar un nuevo link para ${emp.name}? El anterior dejará de funcionar.`)) return;
    try {
      await api.post(`/payroll/employees/${emp.id}/regenerate-link`);
      toast.success("Nuevo link generado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openCreate = () => { setEditing(null); setForm(EMPTY_EMP); setEmpOpen(true); };
  const openEdit = (emp) => { setEditing(emp); setForm({ ...EMPTY_EMP, ...emp }); setEmpOpen(true); };

  const submit = async () => {
    try {
      const payload = { ...form, salary: parseFloat(form.salary) || 0, tolerance_minutes: parseInt(form.tolerance_minutes) || 0 };
      if (editing) {
        await api.put(`/payroll/employees/${editing.id}`, payload);
        toast.success("Empleado actualizado");
      } else {
        await api.post("/payroll/employees", payload);
        toast.success("Empleado registrado — ya puedes copiar su link de ponche");
      }
      setEmpOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (emp) => {
    if (!window.confirm(`¿Eliminar a ${emp.name} de la nómina?`)) return;
    try {
      await api.delete(`/payroll/employees/${emp.id}`);
      toast.success("Empleado eliminado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const generateRun = async () => {
    try {
      const res = await api.post("/payroll/runs", runForm);
      toast.success(`Nómina generada con ${res.data.items.length} empleados`);
      setRunOpen(false);
      load();
      openRun(res.data.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openRun = (id) => {
    api.get(`/payroll/runs/${id}`).then((r) => setRunDetail(r.data)).catch((e) => toast.error(formatApiError(e)));
  };

  const saveRun = async () => {
    try {
      const res = await api.put(`/payroll/runs/${runDetail.id}`, { items: runDetail.items });
      setRunDetail(res.data);
      toast.success("Nómina actualizada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const payRun = async () => {
    try {
      await api.post(`/payroll/runs/${runDetail.id}/pay`);
      toast.success("Nómina marcada como pagada");
      openRun(runDetail.id);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteRun = async (run) => {
    if (!window.confirm("¿Eliminar esta nómina?")) return;
    try {
      await api.delete(`/payroll/runs/${run.id}`);
      toast.success("Nómina eliminada");
      setRunDetail(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setItem = (i, k, v) => setRunDetail((r) => {
    const items = [...r.items];
    items[i] = { ...items[i], [k]: v };
    if (k === "deductions") items[i].net = (parseFloat(items[i].base) || 0) - (parseFloat(v) || 0);
    return { ...r, items };
  });

  return (
    <div data-testid="nomina-page">
      <PageHeader title="Nómina y Ponches" subtitle="Empleados, control de entrada/salida con link personalizado y generación de nómina" />

      <Tabs defaultValue="empleados" onValueChange={(v) => { if (v === "ponches") loadPunches(); if (v === "nomina") load(); }}>
        <TabsList className="flex flex-wrap h-auto gap-1 mb-4">
          <TabsTrigger value="empleados" data-testid="nomina-tab-empleados">Empleados</TabsTrigger>
          <TabsTrigger value="ponches" data-testid="nomina-tab-ponches">Ponches</TabsTrigger>
          <TabsTrigger value="nomina" data-testid="nomina-tab-nomina">Nómina</TabsTrigger>
        </TabsList>

        <TabsContent value="empleados">
          <div className="flex justify-end mb-3">
            <Button className="bg-blue-800 hover:bg-blue-700" data-testid="create-employee-button" onClick={openCreate}>
              <Plus className="w-4 h-4 mr-1" /> Nuevo empleado
            </Button>
          </div>
          <DataTable testid="employees-table" searchable searchPlaceholder="Buscar empleado…" rows={employees} columns={[
            { key: "name", label: "Empleado", render: (e) => <span className="font-medium text-slate-900">{e.name}</span> },
            { key: "position", label: "Cargo" },
            { key: "salary", label: "Salario", render: (e) => fmt(e.salary) },
            { key: "pay_frequency", label: "Frecuencia" },
            { key: "horario", label: "Horario", render: (e) => <span className="font-mono text-xs">{e.expected_start}–{e.expected_end} (+{e.tolerance_minutes}m)</span> },
            { key: "active", label: "Estado", render: (e) => <StatusBadge value={e.active ? "activo" : "retirado"} /> },
            {
              key: "_a", label: "Link de ponche",
              render: (e) => (
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" data-testid={`punch-link-${e.id}`} onClick={() => copyLink(e)}>
                    <Link2 className="w-4 h-4 mr-1" /> Copiar
                  </Button>
                  <Button size="sm" variant="ghost" title="Regenerar link" data-testid={`punch-regen-${e.id}`} onClick={() => regenerate(e)}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              ),
            },
            {
              key: "_b", label: "",
              render: (e) => (
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" data-testid={`edit-employee-${e.id}`} onClick={() => openEdit(e)}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-rose-600" data-testid={`delete-employee-${e.id}`} onClick={() => remove(e)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              ),
            },
          ]} />
        </TabsContent>

        <TabsContent value="ponches">
          <div className="flex items-center gap-3 mb-3">
            <Label className="!mt-0 text-sm">Mes</Label>
            <Input type="month" className="w-44" data-testid="punches-month" value={month} onChange={(e) => setMonth(e.target.value)} />
          </div>
          <DataTable testid="punches-table" searchable searchPlaceholder="Buscar ponche…" rows={punches} columns={[
            { key: "employee_name", label: "Empleado", render: (p) => <span className="font-medium text-slate-900">{p.employee_name}</span> },
            { key: "date", label: "Fecha" },
            { key: "check_in", label: "Entrada", render: (p) => <span className="font-mono text-xs">{fmtTime(p.check_in)}</span> },
            { key: "check_out", label: "Salida", render: (p) => <span className="font-mono text-xs">{fmtTime(p.check_out)}</span> },
            {
              key: "late_minutes", label: "Tardanza",
              render: (p) => p.late_minutes > 0
                ? <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200" data-testid={`late-${p.id}`}>{p.late_minutes} min</span>
                : <span className="text-xs text-emerald-600">A tiempo</span>,
            },
          ]} />
        </TabsContent>

        <TabsContent value="nomina">
          <div className="flex justify-end mb-3">
            <Button className="bg-blue-800 hover:bg-blue-700" data-testid="generate-run-button" onClick={() => setRunOpen(true)}>
              <Plus className="w-4 h-4 mr-1" /> Generar nómina
            </Button>
          </div>
          <DataTable testid="payroll-runs-table" rows={runs} columns={[
            { key: "period", label: "Período", render: (r) => <span className="font-mono text-xs">{r.period}</span> },
            { key: "period_type", label: "Tipo", render: (r) => PTYPE_LABEL[r.period_type] || r.period_type },
            { key: "employee_count", label: "Empleados" },
            { key: "total", label: "Total", render: (r) => <span className="font-semibold">{fmt(r.total)}</span> },
            { key: "status", label: "Estado", render: (r) => <StatusBadge value={r.status} /> },
            {
              key: "_a", label: "",
              render: (r) => (
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" data-testid={`view-run-${r.id}`} onClick={() => openRun(r.id)}><Eye className="w-4 h-4" /></Button>
                  {r.status !== "pagada" && (
                    <Button size="sm" variant="ghost" className="text-rose-600" data-testid={`delete-run-${r.id}`} onClick={() => deleteRun(r)}><Trash2 className="w-4 h-4" /></Button>
                  )}
                </div>
              ),
            },
          ]} />
        </TabsContent>
      </Tabs>

      <Dialog open={empOpen} onOpenChange={setEmpOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar" : "Nuevo"} empleado</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2"><Label>Nombre completo</Label><Input data-testid="emp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Cédula/Documento</Label><Input data-testid="emp-document" value={form.document} onChange={(e) => setForm({ ...form, document: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Cargo</Label><Input data-testid="emp-position" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Teléfono</Label><Input data-testid="emp-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Salario mensual (RD$)</Label><Input type="number" data-testid="emp-salary" value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label>Frecuencia de pago</Label>
                <Select value={form.pay_frequency} onValueChange={(v) => setForm({ ...form, pay_frequency: v })}>
                  <SelectTrigger data-testid="emp-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Mensual">Mensual</SelectItem>
                    <SelectItem value="Quincenal">Quincenal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <h3 className="text-sm font-semibold text-slate-800 border-b border-slate-200 pb-2">Horario personalizado (para tardanzas en el ponche)</h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Entrada</Label><Input type="time" data-testid="emp-expected-start" value={form.expected_start} onChange={(e) => setForm({ ...form, expected_start: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Salida</Label><Input type="time" data-testid="emp-expected-end" value={form.expected_end} onChange={(e) => setForm({ ...form, expected_end: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Tolerancia (min)</Label><Input type="number" data-testid="emp-tolerance" value={form.tolerance_minutes} onChange={(e) => setForm({ ...form, tolerance_minutes: e.target.value })} /></div>
            </div>
            <div className="flex items-center gap-3">
              <Switch data-testid="emp-active" checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <Label className="!mt-0">Activo (puede ponchar y aparece en la nómina)</Label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" data-testid="cancel-employee" onClick={() => setEmpOpen(false)}>Cancelar</Button>
            <Button className="bg-blue-800 hover:bg-blue-700" data-testid="submit-employee" onClick={submit}>{editing ? "Guardar cambios" : "Registrar"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={runOpen} onOpenChange={setRunOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Generar nómina</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label>Período</Label><Input type="month" data-testid="run-period" value={runForm.period} onChange={(e) => setRunForm({ ...runForm, period: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={runForm.period_type} onValueChange={(v) => setRunForm({ ...runForm, period_type: v })}>
                <SelectTrigger data-testid="run-period-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mensual">Mensual (salario completo)</SelectItem>
                  <SelectItem value="quincena-1">1ra Quincena (días 1–15, medio salario)</SelectItem>
                  <SelectItem value="quincena-2">2da Quincena (días 16–fin, medio salario)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-slate-400">Se calculan días trabajados y tardanzas desde los ponches del período.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" data-testid="cancel-run" onClick={() => setRunOpen(false)}>Cancelar</Button>
            <Button className="bg-blue-800 hover:bg-blue-700" data-testid="submit-run" onClick={generateRun}>Generar</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!runDetail} onOpenChange={() => setRunDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          {runDetail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  Nómina {runDetail.period} · {PTYPE_LABEL[runDetail.period_type]}
                  <StatusBadge value={runDetail.status} />
                </DialogTitle>
              </DialogHeader>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="run-detail-table">
                  <thead>
                    <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <th className="py-2 pr-3">Empleado</th><th className="py-2 pr-3">Días</th><th className="py-2 pr-3">Tardanza</th>
                      <th className="py-2 pr-3">Base</th><th className="py-2 pr-3">Deducción</th><th className="py-2 pr-3">Nota</th><th className="py-2">Neto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runDetail.items.map((it, i) => (
                      <tr key={it.employee_id} className="border-b border-slate-100">
                        <td className="py-2 pr-3"><p className="font-medium text-slate-900">{it.name}</p><p className="text-xs text-slate-400">{it.position}</p></td>
                        <td className="py-2 pr-3">{it.days_worked}</td>
                        <td className="py-2 pr-3">{it.late_minutes > 0 ? <span className="text-rose-600 text-xs font-medium">{it.late_minutes} min</span> : "—"}</td>
                        <td className="py-2 pr-3 font-mono text-xs">{fmt(it.base)}</td>
                        <td className="py-2 pr-3">
                          <Input type="number" className="w-24 h-8" data-testid={`run-deduction-${i}`} value={it.deductions} disabled={runDetail.status === "pagada"}
                            onChange={(e) => setItem(i, "deductions", e.target.value)} />
                        </td>
                        <td className="py-2 pr-3">
                          <Input className="w-32 h-8" data-testid={`run-note-${i}`} value={it.deduction_note} disabled={runDetail.status === "pagada"}
                            onChange={(e) => setItem(i, "deduction_note", e.target.value)} />
                        </td>
                        <td className="py-2 font-semibold font-mono text-xs">{fmt((parseFloat(it.base) || 0) - (parseFloat(it.deductions) || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                <p className="text-sm text-slate-500">Total neto: <span className="font-bold text-slate-900" data-testid="run-total">{fmt(runDetail.items.reduce((a, i) => a + (parseFloat(i.base) || 0) - (parseFloat(i.deductions) || 0), 0))}</span></p>
                <div className="flex gap-2">
                  {runDetail.status !== "pagada" && (
                    <>
                      <Button variant="outline" data-testid="run-save" onClick={saveRun}>Guardar deducciones</Button>
                      <Button className="bg-emerald-700 hover:bg-emerald-600" data-testid="run-pay" onClick={payRun}>
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Marcar como pagada
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
